import { useEffect, useLayoutEffect, useState } from 'react'

interface UseCountUpParams {
  endValue: number
  shouldStart: boolean
  durationMs?: number
}

const easeOutQuart = (progress: number) => 1 - (1 - progress) ** 4

const useIsomorphicLayoutEffect =
  typeof window !== 'undefined' ? useLayoutEffect : useEffect

export const useCountUp = ({
  endValue,
  shouldStart,
  durationMs = 1500,
}: UseCountUpParams) => {
  const [currentValue, setCurrentValue] = useState(endValue)
  const [hasAnimated, setHasAnimated] = useState(false)

  useEffect(() => {
    setCurrentValue(endValue)
    setHasAnimated(false)
  }, [endValue])

  useIsomorphicLayoutEffect(() => {
    if (!shouldStart || hasAnimated) {
      return
    }

    if (typeof window === 'undefined') {
      setCurrentValue(endValue)
      setHasAnimated(true)

      return
    }

    if (
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      setCurrentValue(endValue)
      setHasAnimated(true)

      return
    }

    let frameId = 0
    const animationStart = window.performance.now()

    setCurrentValue(0)

    const animate = (timestamp: number) => {
      const progress = Math.min((timestamp - animationStart) / durationMs, 1)
      const easedProgress = easeOutQuart(progress)

      setCurrentValue(Math.round(endValue * easedProgress))

      if (progress < 1) {
        frameId = window.requestAnimationFrame(animate)

        return
      }

      setCurrentValue(endValue)
      setHasAnimated(true)
    }

    frameId = window.requestAnimationFrame(animate)

    return () => {
      window.cancelAnimationFrame(frameId)
    }
  }, [durationMs, endValue, hasAnimated, shouldStart])

  return currentValue
}
